import { onMounted, onUnmounted } from "vue";
import { useAuth } from "@/stores/auth";
import {
  isOAuthTokenExpired,
  getOAuthAccessToken,
  getOAuthRefreshToken,
  refreshAccessToken,
  storeOAuthTokens,
} from "@/service/http/oauthService";
import { getUserFromSession, clearSession } from "@/utils/session";
import { logger } from "@/utils/logger";
import { getCookie } from "@/utils/cookies";

/**
 * Token Monitor Composable
 *
 * Periodically checks token validity and attempts refresh before forcing logout.
 * Also implements preemptive token refresh to prevent expiration during user activity.
 * This provides an additional security layer beyond HTTP interceptors.
 *
 * @param checkInterval - Interval in milliseconds to check token (default: 30 seconds)
 */
export function useTokenMonitor(checkInterval = 30000) {
  let intervalId: number | null = null;
  const authStore = useAuth();

  /**
   * Get time until token expiration in milliseconds
   * @returns milliseconds until expiration, or null if unknown
   */
  function getTokenExpiresIn(): number | null {
    const expiresAt = getCookie("oauth_expires_at");
    if (!expiresAt) return null;
    return parseInt(expiresAt, 10) - Date.now();
  }

  /**
   * Attempt to refresh the access token
   * @returns true if refresh succeeded, false otherwise
   */
  async function attemptTokenRefresh(): Promise<boolean> {
    const refreshToken = getOAuthRefreshToken();
    if (!refreshToken) {
      logger.debug("[TokenMonitor] No refresh token available");
      return false;
    }

    try {
      logger.debug("[TokenMonitor] Attempting to refresh token...");
      const newTokens = await refreshAccessToken(refreshToken);
      storeOAuthTokens(newTokens);
      logger.info("[TokenMonitor] Token refreshed successfully");
      return true;
    } catch (err) {
      logger.error("[TokenMonitor] Token refresh failed:", err);
      return false;
    }
  }

  /**
   * Check if current session and token are valid
   * Attempts token refresh before forcing logout
   */
  async function checkTokenValidity(): Promise<void> {
    // Skip check if on auth pages
    const currentPath = window.location?.pathname || "";
    if (currentPath.startsWith("/auth")) {
      return;
    }

    // Check if user session exists
    const userData = getUserFromSession();
    if (!userData) {
      logger.warn("[TokenMonitor] No user session found, forcing logout");
      forceLogout("no_session");
      return;
    }

    // Check OAuth token if available
    const accessToken = getOAuthAccessToken();
    if (accessToken) {
      // Preemptive refresh: if token expires in less than 60 seconds, refresh it now
      const expiresIn = getTokenExpiresIn();
      if (expiresIn !== null && expiresIn < 60000 && expiresIn > 0) {
        logger.info(
          "[TokenMonitor] Token expiring soon, attempting preemptive refresh...",
        );
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          return; // Successfully refreshed, continue monitoring
        }
        // If preemptive refresh failed, continue to expiration check
      }

      // Check if token is expired
      if (isOAuthTokenExpired()) {
        logger.warn(
          "[TokenMonitor] OAuth token expired, attempting refresh...",
        );
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          return; // Successfully refreshed, continue monitoring
        }
        // Refresh failed, force logout
        logger.warn("[TokenMonitor] Token refresh failed, forcing logout");
        forceLogout("token_expired");
        return;
      }
    }

    // Token is valid, continue monitoring
    logger.debug("[TokenMonitor] Token check passed");
  }

  /**
   * Force logout and redirect to login page
   */
  function forceLogout(reason: string): void {
    // Clear all auth state
    clearSession();
    authStore.clearAuth();

    // Stop monitoring
    stopMonitoring();

    // Redirect to login with reason
    window.location.href = `/auth/login?reason=${reason}`;
  }

  /**
   * Start token monitoring
   */
  function startMonitoring(): void {
    if (intervalId !== null) {
      return; // Already monitoring
    }

    logger.info(
      `[TokenMonitor] Starting token monitoring (interval: ${checkInterval}ms)`,
    );

    // Initial check
    checkTokenValidity();

    // Set up periodic checks
    intervalId = window.setInterval(() => {
      checkTokenValidity();
    }, checkInterval);
  }

  /**
   * Stop token monitoring
   */
  function stopMonitoring(): void {
    if (intervalId !== null) {
      logger.info("[TokenMonitor] Stopping token monitoring");
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  // Auto-start monitoring on mount
  onMounted(() => {
    startMonitoring();
  });

  // Auto-stop monitoring on unmount
  onUnmounted(() => {
    stopMonitoring();
  });

  return {
    startMonitoring,
    stopMonitoring,
    checkTokenValidity,
  };
}
